// Layout: Intro Animation
// GSAP ScrollTrigger pinned section with 3-phase scroll animation + word rotation

import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

document.addEventListener("DOMContentLoaded", function () {
	const section = document.querySelector(".intro-animation");
	if (!section) return;

	// --- Admin bar offset (32px desktop, 46px mobile, 0 if logged out) ---
	const adminBar = document.getElementById("wpadminbar");
	const adminBarHeight = adminBar ? adminBar.offsetHeight : 0;
	if (adminBarHeight > 0) {
		section.style.height = `calc(100vh - ${adminBarHeight}px)`;
	}

	// --- Element references ---
	const desktopVideo = section.querySelector(".intro-animation__video--desktop");
	const mobileVideo = section.querySelector(".intro-animation__video--mobile");
	const overlay = section.querySelector(".intro-animation__overlay");
	const logo = section.querySelector(".intro-animation__logo");
	const headlineWrap = section.querySelector(
		".intro-animation__headline-wrap"
	);
	const chevron = section.querySelector(".intro-animation__chevron--up");
	const chevronDown = section.querySelector(
		".intro-animation__chevron--down:not(.intro-animation__chevron--first)"
	);
	const chevronFirst = section.querySelector(".intro-animation__chevron--first");
	const description = section.querySelector(".intro-animation__description");
	const tagline = section.querySelector(".intro-animation__tagline");
	const cta = section.querySelector(".intro-animation__cta");
	const rotatingWord = section.querySelector(
		".intro-animation__rotating-word"
	);

	// --- Autoplay videos (desktop + mobile) ---
	function tryAutoplay(videoEl) {
		if (!videoEl) return;
		videoEl.muted = true;
		const playPromise = videoEl.play();
		if (playPromise !== undefined) {
			playPromise.catch(function () {
				// Autoplay blocked — hide video, show corresponding fallback
				videoEl.classList.add("hidden");
				const isMobile = videoEl.classList.contains("intro-animation__video--mobile");
				const fallback = section.querySelector(
					isMobile ? ".intro-animation__fallback--mobile" : ".intro-animation__fallback--desktop"
				);
				if (fallback) fallback.classList.remove("hidden");
			});
		}
	}

	tryAutoplay(desktopVideo);
	tryAutoplay(mobileVideo);

	// --- Set initial states (GSAP controls all opacity) ---
	// CSS hides overlay/logo/headline/chevron/description/cta via opacity:0 to prevent flash
	// before GSAP initializes. GSAP then takes over.
	gsap.set(overlay, { yPercent: 100, opacity: 0.85, pointerEvents: "none" });
	gsap.set(logo, { opacity: 0, y: -20 });
	if (headlineWrap) gsap.set(headlineWrap, { opacity: 0, y: 30 });
	if (chevron) gsap.set(chevron, { opacity: 0, pointerEvents: "none" });
	if (chevronDown) gsap.set(chevronDown, { opacity: 0, pointerEvents: "none" });
	// First-screen chevron is visible on load (CSS hides it until GSAP initializes)
	if (chevronFirst) gsap.set(chevronFirst, { opacity: 1, pointerEvents: "auto" });
	if (description) gsap.set(description, { yPercent: 100, opacity: 1 });
	if (cta) gsap.set(cta, { yPercent: 100, opacity: 1 });
	// Tagline is the first text (Phase 1) — visible on load, no fade-in on scroll
	if (tagline) gsap.set(tagline, { opacity: 1 });

	// --- Word rotation with character-by-character CSS animation ---
	const wordsData = JSON.parse(section.dataset.rotatingWords || "[]");
	const defaultWord = section.dataset.rotatingDefault || "";
	let currentWordIndex = 0;
	var allWords =
		rotatingWord && wordsData.length > 0
			? [defaultWord, ...wordsData]
			: [];

	function wrapChars(el) {
		var text = el.textContent;
		// Clear children using DOM methods
		while (el.firstChild) {
			el.removeChild(el.firstChild);
		}
		for (var i = 0; i < text.length; i++) {
			var span = document.createElement("span");
			span.style.display = "inline-block";
			span.style.transition =
				"opacity 0.3s ease, transform 0.3s ease";
			span.style.transitionDelay = i * 0.03 + "s";
			span.textContent = text[i] === " " ? "\u00A0" : text[i];
			el.appendChild(span);
		}
		return el.querySelectorAll("span");
	}

	function cycleWord() {
		if (!rotatingWord) return;
		// Stop cycling when headline is fully invisible
		var wrapOpacity = headlineWrap
			? parseFloat(headlineWrap.style.opacity)
			: 1;
		if (wrapOpacity === 0) {
			wordRotationPlaying = false;
			return;
		}

		// Wrap current text in char spans at full visibility
		var outChars = wrapChars(rotatingWord);
		// Force reflow so browser registers the initial visible state
		rotatingWord.offsetHeight;
		// Now animate out
		outChars.forEach(function (span) {
			span.style.opacity = "0";
			span.style.transform = "translateY(-15px)";
		});

		// After out animation completes, swap text and animate in
		var outDuration = outChars.length * 30 + 300;
		setTimeout(function () {
			// Swap to next word
			currentWordIndex = (currentWordIndex + 1) % allWords.length;

			// Clear and rebuild with new text — start hidden
			var text = allWords[currentWordIndex];
			while (rotatingWord.firstChild) {
				rotatingWord.removeChild(rotatingWord.firstChild);
			}
			var inChars = [];
			for (var i = 0; i < text.length; i++) {
				var span = document.createElement("span");
				span.style.display = "inline-block";
				span.style.opacity = "0";
				span.style.transform = "translateY(15px)";
				span.textContent =
					text[i] === " " ? "\u00A0" : text[i];
				rotatingWord.appendChild(span);
				inChars.push(span);
			}

			// Force reflow, then add transitions and animate in
			rotatingWord.offsetHeight;
			inChars.forEach(function (span, idx) {
				span.style.transition =
					"opacity 0.4s ease, transform 0.4s ease";
				span.style.transitionDelay = idx * 0.03 + "s";
				span.style.opacity = "1";
				span.style.transform = "translateY(0)";
			});

			// Schedule next cycle
			var inDuration = inChars.length * 30 + 400;
			setTimeout(cycleWord, inDuration + 1000);
		}, outDuration);
	}

	function startWordRotation() {
		if (allWords.length === 0) return;
		setTimeout(cycleWord, 1000);
	}

	// Track whether word rotation is playing
	let wordRotationPlaying = false;

	// --- Main scroll timeline (pinned, scrub) ---
	const tl = gsap.timeline({
		scrollTrigger: {
			trigger: section,
			start: `top ${adminBarHeight}px`,
			end: "+=300%",
			pin: true,
			scrub: 0.5,
			anticipatePin: 1,
			onUpdate: function (self) {
				// Start word rotation when headline is visible, restart if scrolled back
				if (allWords.length === 0) return;
				if (!wordRotationPlaying && self.progress >= 0.15 && self.progress < 0.6) {
					startWordRotation();
					wordRotationPlaying = true;
				}
			},
		},
	});

	// === PHASE 1: Tagline is already visible on load (no fade-in) ===

	// === PHASE 1 → PHASE 2 (0% to 30% of scroll) ===
	// Tagline slides from center to the bottom starting the instant scrolling begins,
	// so it never sits frozen in the center waiting for the headline phase.
	if (tagline) {
		tl.to(
			tagline,
			{
				top: "calc(100% - 48px)",
				duration: 0.3,
				ease: "power2.inOut",
			},
			0
		);
	}

	// First-screen chevron fades out as soon as scrolling starts
	if (chevronFirst) {
		tl.to(
			chevronFirst,
			{ opacity: 0, pointerEvents: "none", duration: 0.1, ease: "power1.out" },
			0
		);
	}

	// Logo fades in
	tl.to(logo, { opacity: 1, y: 0, duration: 0.2 }, 0.18);

	// Headline fades in
	if (headlineWrap) {
		tl.to(headlineWrap, { opacity: 1, y: 0, duration: 0.25 }, 0.2);
	}

	// === PHASE 2 → PHASE 3 (42% to 60% of scroll) ===
	// Fade out Phase 2 content as the overlay slides up. Logo/headline/tagline sit
	// ABOVE the overlay (z-20 vs z-10), so their fades must complete by the time the
	// overlay visually settles (~0.52 with power3.out) or they linger on top of it.
	tl.to(logo, { opacity: 0, duration: 0.08 }, 0.42);
	if (headlineWrap) {
		tl.to(headlineWrap, { opacity: 0, duration: 0.08 }, 0.42);
	}
	// Tagline fades out at the bottom as the overlay arrives
	if (tagline) {
		tl.to(tagline, { opacity: 0, duration: 0.08, ease: "power1.out" }, 0.42);
	}

	// Overlay + Phase 3 content slides up from bottom to cover Phase 2.
	// The slide-in finishes at 0.6 (snappy power3.out so it decisively arrives),
	// leaving 0.6 → 1.0 (~40% of the pinned scroll) as a held, "locked in" state —
	// long enough to feel deliberately settled, short enough to not feel empty.
	tl.to(overlay, { yPercent: 0, duration: 0.15, ease: "power3.out" }, 0.45);
	tl.to(chevron, { opacity: 1, pointerEvents: "auto", duration: 0.1 }, 0.55);
	if (chevronDown) {
		tl.to(chevronDown, { opacity: 1, pointerEvents: "auto", duration: 0.1 }, 0.55);
	}
	if (description) {
		tl.to(description, { yPercent: 0, duration: 0.15, ease: "power3.out" }, 0.45);
	}
	if (cta) {
		tl.to(cta, { yPercent: 0, duration: 0.15, ease: "power3.out" }, 0.45);
	}

	// --- Hash navigation: re-scroll to target after ScrollTrigger pin spacers exist ---
	// The pinned hero adds 300vh of spacer; the browser's initial hash jump lands
	// before that exists, so the resolved position is wrong. Re-run after refresh.
	if (window.location.hash) {
		if ("scrollRestoration" in history) {
			history.scrollRestoration = "manual";
		}
		ScrollTrigger.addEventListener("refresh", function scrollToHash() {
			ScrollTrigger.removeEventListener("refresh", scrollToHash);
			var target = document.querySelector(window.location.hash);
			if (!target) return;
			gsap.to(window, {
				scrollTo: { y: target, offsetY: adminBarHeight },
				duration: 0.001,
			});
		});
	}

	// --- Chevron click: scroll back to top (video phase) ---
	if (chevron) {
		chevron.style.cursor = "pointer";
		chevron.addEventListener("click", function (e) {
			e.preventDefault();
			e.stopPropagation();
			// Use GSAP scrollTo with ScrollTrigger-aware approach
			var st = tl.scrollTrigger;
			gsap.to(window, {
				scrollTo: st.start,
				duration: 1.5,
				ease: "power2.inOut",
			});
		});
	}

	// --- First-screen chevron click: scroll forward to the headline phase ---
	if (chevronFirst) {
		chevronFirst.style.cursor = "pointer";
		chevronFirst.addEventListener("click", function (e) {
			e.preventDefault();
			e.stopPropagation();
			var st = tl.scrollTrigger;
			gsap.to(window, {
				scrollTo: st.start + (st.end - st.start) * 0.4,
				duration: 1.5,
				ease: "power2.inOut",
			});
		});
	}

	// --- Down chevron click: scroll past the pinned section to the content below ---
	if (chevronDown) {
		chevronDown.style.cursor = "pointer";
		chevronDown.addEventListener("click", function (e) {
			e.preventDefault();
			e.stopPropagation();
			var st = tl.scrollTrigger;
			gsap.to(window, {
				scrollTo: st.end,
				duration: 1.5,
				ease: "power2.inOut",
			});
		});
	}
});
